<script setup lang="ts">
import type { BasePrice } from "~~/shared/types/market.interfaces";
const emit = defineEmits(["close", "beginPurchase", "soldOut"]);
const { getQueueById, cancelQueueTicket } = useQueuesController();
const { getItemPrice } = useItemsController();
const props = defineProps({
  baseMarketItem: {
    type: Object as PropType<BaseMarketItem>,
    required: true,
  },
  queueInfo: {
    type: Object as PropType<{
      position?: number;
      estimatedMaxPrice?: BasePrice;
    }>,
    required: true,
  },
});

const localQueueInfo = ref<{
  position?: number;
  estimatedMaxPrice?: BasePrice;
  number?: number;
  supply?: number;
}>({
  position: props.queueInfo.position,
  estimatedMaxPrice: props.queueInfo.estimatedMaxPrice,
  number: undefined,
  supply: undefined,
});

const initialQueuePosition = ref<number | null>(null);

const queueProgress = computed(() => {
  if (!initialQueuePosition.value) return 0;
  const currentPos = localQueueInfo.value.position ?? 0;
  const progress = (currentPos / initialQueuePosition.value) * 100;
  return Math.max(0, Math.min(100, progress));
});

const isOutOfSupply = computed(() => {
  return (
    localQueueInfo.value.position !== undefined &&
    localQueueInfo.value.number !== undefined &&
    localQueueInfo.value.position > localQueueInfo.value.number
  );
});

const getEstimatedMaxPrice = async (positionOverride?: number) => {
  const positionToUse =
    positionOverride ??
    localQueueInfo.value.position ??
    props.queueInfo.position ??
    1;
  const priceResponse = await getItemPrice(
    positionToUse,
    props.baseMarketItem.id,
    props.baseMarketItem.merchantId
  );

  if (priceResponse && priceResponse.payload) {
    localQueueInfo.value.estimatedMaxPrice = priceResponse.payload.price;
    localQueueInfo.value.number = priceResponse.payload.number;
    localQueueInfo.value.supply = priceResponse.payload.supply;
  }

  if (priceResponse.payload?.number == 0) {
    await cancelQueue();
    emit("soldOut");
  }
};

const getQueueByItem = async () => {
  if (
    props.baseMarketItem &&
    props.baseMarketItem.id &&
    props.baseMarketItem.merchantId
  ) {
    const queue = await getQueueById(
      props.baseMarketItem.id,
      props.baseMarketItem.merchantId
    );
    return queue;
  }
};

const cancelQueue = async () => {
  if (
    props.baseMarketItem &&
    props.baseMarketItem.id &&
    props.baseMarketItem.merchantId
  ) {
    await cancelQueueTicket(
      props.baseMarketItem.id,
      props.baseMarketItem.merchantId
    );
  }
  emit("close");
  window.location.reload();
};

let pollInterval: ReturnType<typeof setInterval>;
const pollQueueInfo = async () => {
  const queue = await getQueueByItem();
  if (queue) {
    if (queue.position === 0) {
      clearInterval(pollInterval);
      emit("beginPurchase");
    }
    if (queue.position !== localQueueInfo.value.position) {
      await getEstimatedMaxPrice(queue.position);
      localQueueInfo.value.position = queue.position;
    } else {
      localQueueInfo.value.position = queue.position;
    }
    if (initialQueuePosition.value === null) {
      initialQueuePosition.value = localQueueInfo.value.position;
    }
  }
};

onMounted(() => {
  pollQueueInfo();
  getEstimatedMaxPrice();
  pollInterval = setInterval(pollQueueInfo, 5000);
  if (localQueueInfo.value.position !== undefined) {
    initialQueuePosition.value = localQueueInfo.value.position;
  }
});

onUnmounted(() => {
  clearInterval(pollInterval);
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-2">
      <h2
        class="text-neutral-300 text-xl font-bold py-2 border-b border-b-neutral-500">
        {{ $t("main.modalBuyQueue.heading") }}
      </h2>
      <div
        class="flex items-center flex-wrap gap-4 border-b border-b-neutral-500 pb-2">
        <div class="border border-secondary-500">
          <img
            class="w-16 object-contain"
            :src="getPortraitItemImage(baseMarketItem.imageId)"
            @error="
              (e: any) =>
                ((e.target as HTMLImageElement).src = getPortraitItemImage())
            " />
        </div>
        <div class="flex flex-col justify-center gap-1 min-h-full">
          <p class="text-neutral-300 font-bold">
            {{ $t(`${baseMarketItem.translationId}.title`) }}
            {{ $t(`${baseMarketItem.translationId}.subtitle`) }}
          </p>
          <p
            class="w-max h-6 px-2 text-xs text-neutral-300 bg-secondary-500 flex items-center">
            {{
              $t(
                `${baseMarketItem.merchantId}.property.categoryId.values.${baseMarketItem.categoryId}`
              )
            }}
          </p>
        </div>
      </div>
      <p
        class="text-neutral-300 text-center"
        v-if="localQueueInfo.position !== undefined">
        {{
          $t("main.modalBuyQueue.currentPosition", {
            position: localQueueInfo.position,
          })
        }}
      </p>
      <div class="w-full px-4 py-2">
        <div v-if="localQueueInfo.position !== undefined">
          <div
            class="w-full h-4 rounded-lg overflow-hidden"
            style="background: #1b1b1d">
            <div
              class="h-full queue-progress-bar queue-progress-bar-striped transition-all duration-500"
              :style="{ width: queueProgress + '%' }"></div>
          </div>
        </div>
      </div>
      <p
        class="text-neutral-300 text-sm border-b border-b-neutral-500 text-center pb-2"
        v-html="$t('main.modalBuyQueue.positionInfo')"></p>
    </div>
    <div class="bg-[#2B2B2B] py-2 pr-6 mt-4 flex rounded">
      <NuxtIcon
        :name="isOutOfSupply ? 'mdi:warning' : 'mdi:information'"
        class="w-8"
        :class="isOutOfSupply ? 'text-warning-300' : 'text-rarity-exotic'" />
      <p
        :class="
          isOutOfSupply
            ? 'text-warning-300 text-sm pr-8'
            : 'text-rarity-exotic text-sm pr-8'
        ">
        <template v-if="isOutOfSupply">
          {{
            $t("main.modalBuyQueue.outOfSupplyWarning", {
              position: localQueueInfo.position,
              number: localQueueInfo.number,
              supply: localQueueInfo.supply,
            })
          }}
        </template>
        <template v-else>
          {{ $t("main.modalBuyQueue.estimatedMaxPricePrefix") }}
          <span class="font-bold">
            {{ roundNum(localQueueInfo.estimatedMaxPrice?.GFAL || 0, 2) }} GFAL
            <span v-if="localQueueInfo.estimatedMaxPrice?.USD">
              &nbsp;≈&nbsp;$
              {{ roundNum(localQueueInfo.estimatedMaxPrice?.USD || 0, 2) }}
            </span>
          </span>
        </template>
      </p>
    </div>
    <div class="mt-5 mb-2 sm:mt-6 flex">
      <div class="flex items-center gap-4">
        <BaseButtonCancel
          id="cancel_buy_button"
          data-test="button-cancel-buy"
          type="button"
          :text="$t('main.modalBuyBegin.cancel')"
          :contextual-styles="true"
          @click="cancelQueue" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.queue-progress-bar {
  background-color: #b3835f;
  position: relative;
  overflow: hidden;
}

.queue-progress-bar-striped {
  /* Striped gradient */
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.15) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.15) 75%,
    transparent 75%,
    transparent
  );
  background-size: 40px 40px;
  animation: progress-bar-stripes 2s linear infinite;
}

@keyframes progress-bar-stripes {
  from {
    background-position: 40px 0;
  }
  to {
    background-position: 0 0;
  }
}
</style>
